/* /app/frontend/src/pages/communication/ControlCentreTab.jsx — iter-393
 *
 * Portfolio-level Transaction Control Board.  One dense table row
 * per deal ordered by urgency (blocked → overdue → at_risk → on_track
 * → terminal), with a top strip showing the portfolio radar and
 * per-row inline actions (Reply · Call · Request Doc · Open Deal
 * Room).  Row click / expand button opens a side drawer with the
 * full Critical Path detail (owner · waiting · outcome · impact +
 * the same four actions).
 *
 * SSOT: reads from `GET /api/v2/control-centre` which reuses the
 * Deal Room's `blocker_engine.detect_blockers` + resolved-blocker
 * audit trail — no duplicated Critical Path maths.
 *
 * Reply / Call / Request Doc do not have inline composers here.
 * They deep-link to the Deal Room (`/deal-rooms/{id}?action=reply|
 * call|request-doc&blocker={id}`) so we don't fork the existing
 * composer flows.  Open Deal Room jumps to the room's canonical
 * page.
 */
import { createPortal } from "react-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertOctagon, Clock as ClockIcon, ExternalLink, Filter,
  MessageSquare, Phone, FilePlus2, Search, ChevronRight,
  X, Sparkles, ShieldAlert, CheckCircle2, PartyPopper, AlertCircle,
} from "lucide-react";
import { api } from "../../lib/api";


const _BUCKETS = [
  { id: "all",       label: "All",       tone: "bg-slate-100  text-slate-700  ring-slate-200" },
  { id: "blocked",   label: "Blocked",   tone: "bg-rose-50    text-rose-700   ring-rose-200"    },
  { id: "overdue",   label: "Overdue",   tone: "bg-amber-50   text-amber-800  ring-amber-200"   },
  { id: "at_risk",   label: "At Risk",   tone: "bg-amber-50   text-amber-700  ring-amber-200/70"},
  { id: "on_track",  label: "On Track",  tone: "bg-emerald-50 text-emerald-700 ring-emerald-200"},
  { id: "terminal",  label: "Complete",  tone: "bg-slate-100  text-slate-500  ring-slate-200"   },
];


function _healthTone(score) {
  if (score == null) return "text-slate-400";
  if (score >= 75)   return "text-emerald-700 bg-emerald-50";
  if (score >= 50)   return "text-amber-700 bg-amber-50";
  return "text-rose-700 bg-rose-50";
}

function _initials(name) {
  return String(name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

// Critical-path icon follows the blocker's own severity (display only).
function _cpIcon(severity) {
  const sev = String(severity || "").toLowerCase();
  if (["critical", "high", "blocking"].includes(sev)) return { Icon: AlertOctagon, tone: "text-rose-500" };
  if (sev === "warning") return { Icon: AlertCircle, tone: "text-amber-500" };
  return { Icon: ClockIcon, tone: "text-slate-400" };
}

function _daysLabel(days) {
  if (!Number.isFinite(days) || days <= 0) return "Just raised";
  return `${days} ${days === 1 ? "day" : "days"}`;
}


/* Pilot Readiness UX Sweep — mirror the Command Centre owner_role
 * label map so the Communication Centre owner column never renders
 * a raw slug ("SOLICITOR SELLER") and always reads in natural
 * pilot-facing order ("SELLER SOLICITOR").  The CSS still uppercases
 * the string; this map only fixes the WORD ORDER + spelling. */
const _OWNER_ROLE_LABEL = {
  buyer:               "Buyer",
  seller:              "Seller",
  buyer_solicitor:     "Buyer Solicitor",
  seller_solicitor:    "Seller Solicitor",
  solicitor_buyer:     "Buyer Solicitor",
  solicitor_seller:    "Seller Solicitor",
  mortgage_broker:     "Mortgage Broker",
  managing_agent:      "Managing Agent",
  search_provider:     "Search Provider",
  surveyor:            "Surveyor",
  lender:              "Lender",
};

function _formatOwnerRole(role, fallback = "") {
  if (!role) return fallback;
  return _OWNER_ROLE_LABEL[role] || String(role).replace(/_/g, " ");
}

function OverdueLine({ deal, withRisk = false }) {
  const n = deal.exchange_overdue_days;
  return (
    <p className="mt-0.5 text-[11.5px] font-semibold text-rose-600 leading-snug" data-testid={`cc-row-exchange-overdue-${deal.id}`}>
      {withRisk ? "At Risk — " : ""}Exchange target overdue · {n} day{n === 1 ? "" : "s"}
    </p>
  );
}

function _bucketBadge(bucket) {
  const cfg = _BUCKETS.find((b) => b.id === bucket) || _BUCKETS[0];
  return (
    <span
      className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[10px] font-bold uppercase tracking-[0.06em] ring-1 ${cfg.tone}`}
      data-testid={`cc-row-bucket-${bucket}`}
    >
      {cfg.label}
    </span>
  );
}


/* ─── Portfolio Radar ─────────────────────────────────────────────
 * Top strip showing the count of deals in each urgency bucket.
 * Same visual language as the Deal Room Transaction Radar but
 * portfolio-scoped. */
function PortfolioRadar({ counts, activeBucket, onSelectBucket }) {
  const items = [
    { id: "blocked",  label: "Blocked",  hint: "Can't progress",       count: counts?.blocked || 0,  Icon: AlertOctagon, tone: "text-rose-600 bg-rose-50 ring-rose-200"     },
    { id: "overdue",  label: "Overdue",  hint: "Waiting 3+ days",      count: counts?.overdue || 0,  Icon: ClockIcon,    tone: "text-amber-700 bg-amber-50 ring-amber-200"  },
    { id: "at_risk",  label: "At Risk",  hint: "Needs attention",      count: counts?.at_risk || 0,  Icon: ShieldAlert,  tone: "text-amber-600 bg-amber-50 ring-amber-200/70"},
    { id: "on_track", label: "On Track", hint: "No current blockers",  count: counts?.on_track || 0, Icon: CheckCircle2, tone: "text-emerald-600 bg-emerald-50 ring-emerald-200"},
    { id: "terminal", label: "Complete", hint: "Exchanged or complete", count: counts?.terminal || 0, Icon: PartyPopper,  tone: "text-slate-500 bg-slate-100 ring-slate-200" },
  ];
  const total = items.reduce((s, it) => s + it.count, 0);
  return (
    <section
      className="rounded-2xl bg-white/90 backdrop-blur-sm ring-1 ring-slate-200/70 p-4 sm:p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
      data-testid="cc-portfolio-radar"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
          <Sparkles size={16} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-slate-900">Portfolio Radar</div>
          <div className="text-[12px] text-slate-500 mt-0.5">
            What needs intervention right now across every deal in your workspace.
          </div>
        </div>
        <div className="text-right shrink-0" data-testid="cc-radar-total">
          <div className="text-[9.5px] uppercase tracking-[0.08em] font-semibold text-slate-400">Total</div>
          <div className="text-[22px] font-bold text-slate-900 tabular-nums leading-none mt-1">{total}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        {items.map((it) => {
          const active = activeBucket === it.id;
          const Icon = it.Icon;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => onSelectBucket(active ? "all" : it.id)}
              className={`group flex items-center gap-3 rounded-xl px-3.5 py-3 ring-1 transition text-left cf-press ${
                active ? "ring-violet-400 bg-violet-50/70 shadow-sm" : "ring-slate-200/70 bg-white hover:bg-slate-50"
              }`}
              data-testid={`cc-radar-tile-${it.id}`}
              aria-pressed={active}
            >
              <div className={`h-8 w-8 sm:h-10 sm:w-10 shrink-0 rounded-xl ring-1 flex items-center justify-center ${it.tone}`}>
                <Icon size={17} strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500 whitespace-nowrap">
                  {it.label}
                </div>
                <div className="text-[22px] font-bold text-slate-900 tabular-nums leading-none mt-1" data-testid={`cc-radar-count-${it.id}`}>
                  {it.count}
                </div>
                <div className="hidden sm:block text-[11px] text-slate-500 mt-1 truncate">{it.hint}</div>
              </div>
              <ChevronRight size={14} strokeWidth={2.2} className="hidden sm:block shrink-0 text-slate-300 group-hover:text-slate-500 transition-colors" />
            </button>
          );
        })}
      </div>
    </section>
  );
}


/* ─── Row action button ──────────────────────────────────────── */
function RowAction({ Icon, label, onClick, testid, primary }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      title={label}
      aria-label={label}
      className={`inline-flex items-center justify-center h-7 w-7 rounded-md ring-1 transition ${
        primary
          ? "bg-violet-600 text-white ring-violet-600 hover:bg-violet-700"
          : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 hover:text-slate-900"
      }`}
      data-testid={testid}
    >
      <Icon size={13} strokeWidth={2.3} />
    </button>
  );
}


/* ─── Deals table ─────────────────────────────────────────────── */
function DealsTable({ deals, onInspect, onOpenDeal, onDeepLink }) {
  if (!deals || deals.length === 0) {
    return (
      <div
        className="rounded-2xl bg-slate-50/60 ring-1 ring-slate-200/60 px-6 py-12 text-center"
        data-testid="cc-empty"
      >
        <CheckCircle2 className="mx-auto mb-3 text-emerald-500" size={22} strokeWidth={2} />
        <div className="text-[13px] font-semibold text-slate-700">No deals matching this filter</div>
        <div className="mt-1 text-[12px] text-slate-500 max-w-md mx-auto">
          Broaden the filter or clear the search to see the wider portfolio.
        </div>
      </div>
    );
  }
  return (
    <div
      className="rounded-2xl bg-white ring-1 ring-slate-200/70 overflow-hidden"
      data-testid="cc-deals-table"
    >
      {/* Header — iPad landscape (lg): compact six-column table */}
      <div
        className="hidden lg:grid xl:hidden grid-cols-[minmax(0,1.6fr)_minmax(0,1.8fr)_minmax(0,1.2fr)_64px_minmax(0,1.4fr)_188px] gap-3 items-center px-4 py-3 border-b border-slate-100 bg-slate-50/60"
        data-testid="cc-table-header-compact"
      >
        {["Property", "Critical Path", "Owner", "Waiting", "Next Action"].map((h) => (
          <div key={h} className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500">{h}</div>
        ))}
        <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500 text-right">Actions</div>
      </div>
      {/* Header — desktop (xl): approved eight-column table */}
      <div
        className="hidden xl:grid grid-cols-[minmax(0,2fr)_minmax(0,1.1fr)_minmax(0,2fr)_minmax(0,1.3fr)_72px_minmax(0,1.6fr)_56px_196px] gap-3 items-center px-4 py-3 border-b border-slate-100 bg-slate-50/60"
        data-testid="cc-table-header"
      >
        <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500">Property</div>
        <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500">Current Milestone</div>
        <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500">Critical Path</div>
        <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500">Owner</div>
        <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500">Waiting</div>
        <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500">Next Action</div>
        <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500 text-center">Health</div>
        <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500 text-right">Actions</div>
      </div>
      <ul className="divide-y divide-slate-100">
        {deals.map((d) => (
          <DealRow
            key={d.id}
            deal={d}
            onInspect={onInspect}
            onOpenDeal={onOpenDeal}
            onDeepLink={onDeepLink}
          />
        ))}
      </ul>
    </div>
  );
}


// Small labels for the iPad-portrait card layout only (phone and table layouts unchanged).
const CellLabel = ({ children }) => (
  <span className="hidden md:block lg:hidden mb-1 text-[10px] uppercase tracking-[0.08em] font-semibold text-slate-400">{children}</span>
);

function DealRow({ deal, onInspect, onOpenDeal, onDeepLink }) {
  const cp = deal.critical_path;
  const address = deal.property_address || deal.name || "Untitled property";
  return (
    <li
      className="grid grid-cols-2 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1.8fr)_minmax(0,1.2fr)_64px_minmax(0,1.4fr)_188px] lg:gap-3 xl:grid-cols-[minmax(0,2fr)_minmax(0,1.1fr)_minmax(0,2fr)_minmax(0,1.3fr)_72px_minmax(0,1.6fr)_56px_196px] gap-x-4 gap-y-2.5 xl:gap-3 items-center px-4 py-3.5 hover:bg-slate-50/70 cursor-pointer transition-colors"
      data-testid={`cc-row-${deal.id}`}
      data-bucket={deal.urgency_bucket}
      onClick={() => onInspect(deal)}
    >
      {/* Property */}
      <div className="min-w-0 col-span-2 lg:col-span-1">
        <div className="flex flex-col items-start gap-1 min-w-0">
          {_bucketBadge(deal.urgency_bucket)}
          <span className="text-[13px] font-semibold text-slate-900 line-clamp-2 break-words leading-snug max-w-full" title={address}>
            {address}
          </span>
        </div>
        {deal.property_postcode && (
          <p className="mt-0.5 text-[11px] text-slate-500 truncate">{deal.property_postcode}</p>
        )}
        {/* iPad landscape: milestone + health fold into the property cell */}
        <div className="hidden lg:flex xl:hidden items-center gap-2 mt-1 min-w-0">
          <span className="text-[11.5px] text-slate-600 truncate" title={deal.stage_label}>{deal.stage_label}</span>
          <span className={`shrink-0 inline-flex items-center justify-center min-w-[30px] h-5 px-1.5 rounded-full text-[11px] font-bold tabular-nums ${_healthTone(deal.chain_health)}`} title="Health">
            {deal.chain_health ?? "—"}
          </span>
        </div>
      </div>
      {/* Current Milestone */}
      <div className="min-w-0 lg:hidden xl:block">
        <CellLabel>Current milestone</CellLabel>
        <p className="text-[12.5px] font-medium text-slate-800 line-clamp-2 leading-snug" title={deal.stage_label} data-testid={`cc-row-stage-${deal.id}`}>
          {deal.stage_label}
        </p>
      </div>
      {/* Critical Path */}
      <div className="min-w-0" data-testid={`cc-row-critical-path-${deal.id}`}>
        <CellLabel>Critical path</CellLabel>
        {cp ? (
          <>
            {(() => { const { Icon, tone } = _cpIcon(cp.severity); return (
              <p className="text-[12.5px] font-semibold text-slate-900 flex items-start gap-1.5 leading-snug" title={cp.title}>
                <Icon size={12} strokeWidth={2.4} className={`shrink-0 mt-[2px] ${tone}`} />
                <span className="line-clamp-2 break-words">{cp.title}</span>
              </p>
            ); })()}
            {deal.exchange_overdue_days > 0 ? (
              <div className="pl-[18px]"><OverdueLine deal={deal} /></div>
            ) : (
              <p className="mt-0.5 pl-[18px] text-[11.5px] text-slate-500 line-clamp-1" title={cp.impact_label}>
                {cp.impact_label}
              </p>
            )}
          </>
        ) : deal.exchange_overdue_days > 0 ? (
          <OverdueLine deal={deal} withRisk />
        ) : (
          <p className="text-[12px] text-emerald-700 font-medium inline-flex items-center gap-1">
            <CheckCircle2 size={11} strokeWidth={2.4} /> No open blocker
          </p>
        )}
      </div>
      {/* Owner */}
      <div className="min-w-0">
        <CellLabel>Owner</CellLabel>
        {cp?.owner_name ? (
          <div className="flex items-center gap-2 min-w-0" title={`${cp.owner_name}${cp.owner_role ? ` · ${_formatOwnerRole(cp.owner_role)}` : ""}`}>
            <span className="h-7 w-7 shrink-0 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-100 text-[10.5px] font-bold flex lg:hidden xl:flex items-center justify-center" aria-hidden="true">
              {_initials(cp.owner_name)}
            </span>
            <span className="min-w-0">
              <span className="block text-[12.5px] font-medium text-slate-800 truncate">{cp.owner_name}</span>
              {cp.owner_role && (
                <span className="block text-[11px] text-slate-500 truncate">{_formatOwnerRole(cp.owner_role)}</span>
              )}
            </span>
          </div>
        ) : cp ? (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenDeal(deal); }}
              className="text-[12px] font-semibold text-violet-700 hover:text-violet-900"
              title="No single named person holds this role on the deal — assign one in the Deal Room"
              data-testid={`cc-row-assign-owner-${deal.id}`}
            >
              Assign owner
            </button>
            {cp.owner_role && (
              <p className="mt-0.5 text-[10.5px] uppercase tracking-[0.06em] text-slate-500 truncate">
                {_formatOwnerRole(cp.owner_role).toUpperCase()}
              </p>
            )}
          </>
        ) : (
          <span className="text-[11.5px] text-slate-400">—</span>
        )}
      </div>
      {/* Waiting */}
      <div className="min-w-0" data-testid={`cc-row-waiting-${deal.id}`}>
        <CellLabel>Waiting</CellLabel>
        {cp ? (
          <span className={`block leading-tight tabular-nums ${cp.age_days >= 7 ? "text-rose-600" : cp.age_days >= 3 ? "text-amber-700" : "text-slate-700"}`}>
            <span className="block text-[12.5px] font-bold">{_daysLabel(cp.age_days)}</span>
            {cp.age_days > 0 && <span className="block text-[11px] font-medium opacity-80">waiting</span>}
          </span>
        ) : (
          <span className="text-[11.5px] text-slate-400">—</span>
        )}
      </div>
      {/* Next Action */}
      <div className="min-w-0">
        <CellLabel>Next action</CellLabel>
        {cp ? (
          <p className="text-[12px] text-slate-700 line-clamp-2 leading-snug" title={cp.next_action}>
            {cp.next_action}
          </p>
        ) : (
          /* iter-alignment · P · Communication Centre · Final Pilot
             Refinement (2026-03-02) — retire the "Chain moving
             freely" filler.  The "No open blocker" line above +
             the canonical Health score below are the calm on-track
             read; a second sentence added visual noise the pilot
             reviewers flagged. */
          <span className="text-[11.5px] text-slate-300" aria-hidden="true">—</span>
        )}
      </div>
      {/* Health — iter-alignment · Health Score SSOT · chain_health canonical */}
      <div className="lg:hidden xl:block xl:text-center">
        <CellLabel>Health</CellLabel>
        <span className={`inline-flex items-center justify-center min-w-[36px] h-6 px-2 rounded-full text-[12px] font-bold tabular-nums ${_healthTone(deal.chain_health)}`} data-testid={`cc-row-health-${deal.id}`}>
          {deal.chain_health ?? "—"}
        </span>
      </div>
      {/* Actions.
          iter-alignment · P · Communication Centre · Final Pilot
          Refinement (2026-03-02) — a prominent violet Chase CTA is
          rendered ONLY when a canonical `critical_path` exists
          (i.e. a real blocker + owner + waiting party).  On-track
          rows keep the compact secondary action set only, so the
          Chase affordance never fires on a healthy deal.  Chase
          reuses the existing chase deep-link into the Deal Room
          (`action=chase&blocker={id}`) so the Deal Room's own
          chase composer owns the participant / activity write —
          no new endpoint, no new state, no new NBA. */}
      <div className="col-span-2 lg:col-span-1 flex items-center justify-end gap-1.5">
        {cp && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDeepLink(deal, "chase"); }}
            className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md ring-1 text-[11.5px] font-semibold cf-press transition-colors ${
              cp.chase_due
                ? "bg-violet-600 text-white ring-violet-600 hover:bg-violet-700"
                : "bg-white text-violet-700 ring-violet-200 hover:bg-violet-50"
            }`}
            data-chase-due={cp.chase_due ? "true" : "false"}
            title={cp.owner_name ? `Chase ${cp.owner_name}` : "Chase — choose the recipient in the draft"}
            aria-label={cp.owner_name ? `Chase ${cp.owner_name} · ${cp.title}` : `Chase · choose recipient · ${cp.title}`}
            data-testid={`cc-row-action-chase-${deal.id}`}
          >
            Chase
          </button>
        )}
        <RowAction
          Icon={MessageSquare}
          label="Reply"
          onClick={() => onDeepLink(deal, "reply")}
          testid={`cc-row-action-reply-${deal.id}`}
        />
        <RowAction
          Icon={Phone}
          label="Call"
          onClick={() => onDeepLink(deal, "call")}
          testid={`cc-row-action-call-${deal.id}`}
        />
        <RowAction
          Icon={FilePlus2}
          label="Request document"
          onClick={() => onDeepLink(deal, "request-doc")}
          testid={`cc-row-action-request-doc-${deal.id}`}
        />
        <RowAction
          Icon={ExternalLink}
          label="Open deal room"
          onClick={() => onOpenDeal(deal)}
          testid={`cc-row-action-open-deal-${deal.id}`}
        />
      </div>
    </li>
  );
}


/* ─── Detail Drawer ──────────────────────────────────────────── */
function DealDetailDrawer({ deal, onClose, onOpenDeal, onDeepLink }) {
  if (!deal) return null;
  const cp = deal.critical_path;
  // Portalled to <body> so the drawer sits above the fixed top rail (header + Close stay clickable).
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex justify-end bg-slate-900/40"
      data-testid="cc-detail-drawer"
      onClick={onClose}
    >
      <aside
        className="w-full sm:max-w-[520px] bg-white shadow-2xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              {_bucketBadge(deal.urgency_bucket)}
              <h2 className="text-[15px] font-bold text-slate-900 truncate" data-testid="cc-drawer-title">
                {deal.property_address || deal.name}
              </h2>
            </div>
            <p className="mt-1 text-[12px] text-slate-500 truncate">
              {deal.stage_label}
              {deal.property_postcode ? ` · ${deal.property_postcode}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-800 h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"
            aria-label="Close"
            data-testid="cc-drawer-close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="p-5 space-y-5">
          {/* Critical Path card */}
          {cp ? (
            <section
              className="rounded-xl ring-1 ring-rose-200/70 bg-rose-50/40 p-4"
              data-testid="cc-drawer-critical-path"
            >
              <div className="flex items-start gap-2.5">
                <div className="h-7 w-7 rounded-md bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5">
                  <AlertOctagon size={14} strokeWidth={2.4} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-rose-700">
                    Critical Path
                  </div>
                  <div className="text-[14px] font-bold text-slate-900 leading-snug mt-0.5">
                    {cp.title}
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[12px] leading-snug">
                    <div>
                      <dt className="text-slate-400 font-medium uppercase tracking-[0.08em] text-[9.5px]">Owner</dt>
                      <dd className="text-slate-800 font-semibold mt-0.5 truncate" data-testid="cc-drawer-owner">
                        {cp.owner_name || "Assign owner"}
                      </dd>
                      {cp.owner_role && (
                        <dd
                          className="text-[10.5px] uppercase tracking-[0.06em] text-slate-500 mt-0.5 truncate"
                          data-testid="cc-drawer-owner-role"
                        >
                          {_formatOwnerRole(cp.owner_role).toUpperCase()}
                        </dd>
                      )}
                    </div>
                    <div>
                      <dt className="text-slate-400 font-medium uppercase tracking-[0.08em] text-[9.5px]">Waiting</dt>
                      <dd className="text-slate-800 font-semibold mt-0.5" data-testid="cc-drawer-waiting">
                        {_daysLabel(cp.age_days)}
                      </dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-slate-400 font-medium uppercase tracking-[0.08em] text-[9.5px]">Expected Outcome</dt>
                      <dd className="text-slate-700 mt-0.5" data-testid="cc-drawer-outcome">{cp.next_action}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-slate-400 font-medium uppercase tracking-[0.08em] text-[9.5px]">Estimated Impact</dt>
                      <dd className="text-slate-700 mt-0.5" data-testid="cc-drawer-impact">{cp.impact_label}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </section>
          ) : (
            <section
              className={`rounded-xl ring-1 p-4 ${deal.exchange_overdue_days > 0 ? "ring-rose-200/70 bg-rose-50/40" : "ring-emerald-200/70 bg-emerald-50/40"}`}
              data-testid="cc-drawer-clear"
            >
              <div className="flex items-start gap-2.5">
                <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${deal.exchange_overdue_days > 0 ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-600"}`}>
                  {deal.exchange_overdue_days > 0 ? <AlertOctagon size={14} strokeWidth={2.4} /> : <CheckCircle2 size={14} strokeWidth={2.4} />}
                </div>
                {deal.exchange_overdue_days > 0 ? (
                  <div>
                    <div className="text-[14px] font-bold text-slate-900" data-testid="cc-drawer-exchange-overdue">
                      At Risk — Exchange target overdue · {deal.exchange_overdue_days} day{deal.exchange_overdue_days === 1 ? "" : "s"}
                    </div>
                    <p className="mt-1 text-[12px] text-slate-600">No open blocker is recorded. Review the target or the next milestone in the Deal Room.</p>
                  </div>
                ) : (
                <div>
                  <div className="text-[14px] font-bold text-slate-900">No open blocker</div>
                  <p className="mt-0.5 text-[12px] text-slate-600">This chain has no open blocker — track the next milestone from the Deal Room.</p>
                </div>
                )}
              </div>
            </section>
          )}

          {/* Meta row */}
          <section
            className="grid grid-cols-3 gap-2 rounded-xl ring-1 ring-slate-200/70 p-3"
            data-testid="cc-drawer-meta"
          >
            <div>
              <div className="text-[9.5px] uppercase tracking-[0.08em] font-semibold text-slate-400">Health</div>
              <div className={`text-[15px] font-bold tabular-nums mt-0.5 ${_healthTone(deal.chain_health)}`}>
                {deal.chain_health ?? "—"}
              </div>
            </div>
            <div>
              <div className="text-[9.5px] uppercase tracking-[0.08em] font-semibold text-slate-400">Open Commitments</div>
              <div className="text-[15px] font-bold tabular-nums mt-0.5 text-slate-800" data-testid="cc-drawer-open-commitments">
                {deal.open_commitments}
              </div>
            </div>
            <div>
              <div className="text-[9.5px] uppercase tracking-[0.08em] font-semibold text-slate-400">Status</div>
              <div className="text-[12.5px] font-semibold mt-0.5 text-slate-800 truncate capitalize">
                {(deal.status || "active").replace(/_/g, " ")}
              </div>
            </div>
          </section>

          {/* Actions */}
          <section className="flex flex-wrap gap-2" data-testid="cc-drawer-actions">
            <button
              type="button"
              onClick={() => onDeepLink(deal, "reply")}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="cc-drawer-action-reply"
            >
              <MessageSquare size={13} strokeWidth={2.3} /> Reply
            </button>
            <button
              type="button"
              onClick={() => onDeepLink(deal, "call")}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="cc-drawer-action-call"
            >
              <Phone size={13} strokeWidth={2.3} /> Call
            </button>
            <button
              type="button"
              onClick={() => onDeepLink(deal, "request-doc")}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="cc-drawer-action-request-doc"
            >
              <FilePlus2 size={13} strokeWidth={2.3} /> Request Document
            </button>
            <button
              type="button"
              onClick={() => onOpenDeal(deal)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-violet-600 text-white text-[12.5px] font-semibold hover:bg-violet-700 ml-auto"
              data-testid="cc-drawer-action-open-deal"
            >
              Open Deal Room <ChevronRight size={13} strokeWidth={2.4} />
            </button>
          </section>
        </div>
      </aside>
    </div>,
    document.body,
  );
}


/* ─── Root ────────────────────────────────────────────────────── */
export default function ControlCentreTab() {
  const [snapshot, setSnapshot] = useState({ counts: {}, deals: [] });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [bucket, setBucket]     = useState("all");
  const [query, setQuery]       = useState("");
  const [inspected, setInspected] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/api/v2/control-centre");
      setSnapshot(data || { counts: {}, deals: [] });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load the Control Centre snapshot.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (snapshot.deals || []).filter((d) => {
      if (bucket !== "all" && d.urgency_bucket !== bucket) return false;
      if (!q) return true;
      // iter-alignment · P · Communication Centre · Final Pilot
      // Refinement (2026-03-02) — search now spans owner role
      // (formatted human label + raw slug) and the blocker impact
      // string so operators can find rows by participant type
      // ("solicitor") or by milestone context ("searches").
      const cp = d.critical_path;
      const ownerRoleRaw = cp?.owner_role || "";
      const ownerRoleLabel = ownerRoleRaw
        ? _formatOwnerRole(ownerRoleRaw)
        : "";
      const hay = [
        d.property_address, d.property_postcode, d.stage_label,
        cp?.title, cp?.owner_name, cp?.impact_label,
        ownerRoleRaw, ownerRoleLabel,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [snapshot.deals, bucket, query]);

  const onOpenDeal = useCallback((deal) => {
    navigate(`/deal-rooms/${deal.id}`);
  }, [navigate]);

  const onDeepLink = useCallback((deal, action) => {
    const params = new URLSearchParams({ cc: "1", action });
    if (deal.critical_path?.id) params.set("blocker", deal.critical_path.id);
    navigate(`/deal-rooms/${deal.id}?${params.toString()}`);
  }, [navigate]);

  return (
    <div className="space-y-5 min-w-0 max-w-full" data-testid="cc-control-centre-tab">
      <PortfolioRadar
        counts={snapshot.counts}
        activeBucket={bucket}
        onSelectBucket={setBucket}
      />

      {/* Filter bar
       *
       * iter-alignment · P0 · Communication Centre · Responsive Page
       * Fit (2026-03-07) — The buttons row is now `flex-wrap` so
       * filter pills reflow onto a new line at narrow widths
       * instead of scrolling horizontally.  The search input
       * wrapper is `w-full sm:w-auto`: at ≥sm breakpoints it sits
       * to the right at its natural 220px width; below sm it
       * drops to a full-width row (justify-between + parent
       * flex-wrap handle the visual reflow). */}
      <div className="flex items-center justify-between gap-3 flex-wrap min-w-0" data-testid="cc-filter-bar">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {_BUCKETS.map((b) => {
            const active = bucket === b.id;
            const total = b.id === "all"
              ? (snapshot.deals || []).length
              : (snapshot.counts?.[b.id] || 0);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => setBucket(b.id)}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-semibold whitespace-nowrap transition ${
                  active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`cc-filter-${b.id}`}
                aria-pressed={active}
              >
                <Filter size={11} strokeWidth={2.4} className={active ? "opacity-90" : "opacity-70"} />
                {b.label}
                <span className={`ml-0.5 tabular-nums text-[10.5px] ${active ? "text-white/80" : "text-slate-400"}`}>{total}</span>
              </button>
            );
          })}
        </div>
        <div className="relative w-full sm:w-auto sm:min-w-[220px]">
          <Search size={13} strokeWidth={2.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search address, owner…"
            className="pl-7 pr-3 py-1.5 rounded-lg text-[12.5px] border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-violet-300 w-full"
            data-testid="cc-search-input"
          />
        </div>
      </div>

      {/* Body */}
      {loading && (
        <div
          className="rounded-2xl bg-slate-50 px-4 py-10 text-center text-[12.5px] text-slate-500"
          data-testid="cc-loading"
        >
          Loading portfolio…
        </div>
      )}
      {!loading && error && (
        <div
          className="rounded-2xl bg-rose-50 ring-1 ring-rose-200 px-4 py-6 text-center text-[12.5px] text-rose-700"
          data-testid="cc-error"
        >
          {error}
        </div>
      )}
      {!loading && !error && (
        <DealsTable
          deals={filtered}
          onInspect={setInspected}
          onOpenDeal={onOpenDeal}
          onDeepLink={onDeepLink}
        />
      )}

      <DealDetailDrawer
        deal={inspected}
        onClose={() => setInspected(null)}
        onOpenDeal={onOpenDeal}
        onDeepLink={onDeepLink}
      />
    </div>
  );
}
